import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import MegaMenu from "./MegaMenu";
import Modal from "./../../../hooks/Modal";

import NavbarTopBar from "./NavbarTopBar";
import NavbarMainBar from "./NavbarMainBar";
import NavbarMobileMenu from "./NavbarMobileMenu";

import { useUser } from "../UserContext";
import { useNotification } from "./../../../hooks/NotificationContext";
import { useMessage } from "./../../../hooks/MessageContext";
import { useContactForm } from "./../../../hooks/ContactFormContext";
import useCountBump from "../../../hooks/useCountBump";

import { getSettings } from "../../../services/settingsServices";
import { getAllPages } from "./../../../services/pagesPersonnaliseesServices";
import { getAdherentsByUser } from "./../../../services/adherentServices";
import { getExercisesByAdherent } from "./../../../services/prepaPhysiqueServices";
import { toAbsolutePhotoUrl } from "../../../services/settingsPhotoServices";

import {
  DEFAULT_SETTINGS,
  FALLBACK_LOGO,
  SPORT_ICONS,
} from "./navbarData";

const getStoredUserInfo = () => {
  try {
    return (
      JSON.parse(localStorage.getItem("userInfo") || "null") ||
      JSON.parse(localStorage.getItem("user") || "null")
    );
  } catch (error) {
    return null;
  }
};

export default function Navbar2() {
  const navigate = useNavigate();
  const location = useLocation();

  const { user: contextUser, updateUser } = useUser();
  const { unreadCount } = useNotification();
  const { unreadMessageCount } = useMessage();
  const { unreadCountContactForm } = useContactForm();

  const bumpMessages = useCountBump(unreadMessageCount);
  const bumpNotifications = useCountBump(unreadCount);
  const bumpContactForms = useCountBump(unreadCountContactForm);

  const [localUser, setLocalUser] = useState(() => getStoredUserInfo());
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [pagesActives, setPagesActives] = useState([]);
  const [exercicesRestants, setExercicesRestants] = useState(0);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [theme, setTheme] = useState("light");

  const user = contextUser || localUser;
  const isAuthenticated = Boolean(user?.userId || user?.id || user?.email);
  const isAdmin = Number(user?.role_id) === 1;

  const IconComponent = useMemo(() => {
    return SPORT_ICONS[settings.discipline_icone] || SPORT_ICONS.GiKimono;
  }, [settings.discipline_icone]);

  const logoSrc = useMemo(() => {
    return toAbsolutePhotoUrl((settings.logo_club || "").trim()) || FALLBACK_LOGO;
  }, [settings.logo_club]);

  const closeAllMenus = () => {
    setMobileOpen(false);
    setMegaMenuOpen(false);
  };

  const handleLogout = () => {
    ["userInfo", "user", "token"].forEach((key) => localStorage.removeItem(key));
    ["userInfo", "user", "token"].forEach((key) =>
      sessionStorage.removeItem(key)
    );

    setLocalUser(null);
    updateUser(null);
    closeAllMenus();

    navigate("/", { replace: true });
  };

  const handleThemeToggle = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";

    setTheme(nextTheme);
    localStorage.setItem("theme", nextTheme);
    document.documentElement.classList.toggle("dark", nextTheme === "dark");
  };

  const handleScrollToSection = (targetId) => {
    closeAllMenus();

    if (location.pathname !== "/") {
      navigate("/", {
        state: {
          scrollTo: targetId,
        },
      });
      return;
    }

    if (targetId === "accueil") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const section = document.getElementById(targetId);

    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleInscription = (event) => {
    event?.preventDefault?.();

    if (!isAuthenticated) {
      closeAllMenus();
      setLoginModalOpen(true);
      return;
    }

    closeAllMenus();
    navigate("/ajouter-inscription-adherent");
  };

  const handleApplication = () => {
    if (!isAuthenticated) {
      setLoginModalOpen(true);
      return;
    }

    setMobileOpen(false);
    setMegaMenuOpen((previous) => !previous);
  };

  useEffect(() => {
    const storedUserInfo = getStoredUserInfo();

    if (storedUserInfo && !contextUser) {
      updateUser(storedUserInfo);
      setLocalUser(storedUserInfo);
    }
  }, [contextUser, updateUser]);

  useEffect(() => {
    if (contextUser) {
      setLocalUser(contextUser);
    }
  }, [contextUser]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!clubSettings) return;

        setSettings((previous) => ({
          ...previous,
          ...clubSettings,
        }));

        const primaryColor =
          clubSettings.couleur_principale || clubSettings.primary_color;

        if (primaryColor) {
          document.documentElement.style.setProperty(
            "--primary-color",
            primaryColor
          );
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const pages = await getAllPages();
        const actives = Array.isArray(pages)
          ? pages.filter((page) => page.actif)
          : [];

        setPagesActives(actives);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des pages personnalisées :",
          error
        );
      }
    };

    fetchPages();
  }, []);

  useEffect(() => {
    const fetchExercicesRestants = async () => {
      try {
        if (!isAuthenticated) return;

        const adherentsUser = await getAdherentsByUser();

        if (!Array.isArray(adherentsUser)) return;

        let total = 0;

        for (const adherent of adherentsUser) {
          const adherentWithExercises = await getExercisesByAdherent(
            adherent.id
          );

          const exercices = adherentWithExercises?.Exercices;

          if (Array.isArray(exercices)) {
            total += exercices.filter(
              (exercice) =>
                exercice.AdherentPrepaPhysique?.is_done === false
            ).length;
          }
        }

        setExercicesRestants(total);
      } catch (error) {
        console.error("Erreur exercices restants :", error);
      }
    };

    fetchExercicesRestants();
  }, [isAuthenticated]);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia?.(
      "(prefers-color-scheme: dark)"
    )?.matches;

    const initialTheme = savedTheme || (prefersDark ? "dark" : "light");

    setTheme(initialTheme);
    document.documentElement.classList.toggle("dark", initialTheme === "dark");
  }, []);

  useEffect(() => {
    closeAllMenus();
  }, [location.pathname, location.hash]);

  return (
    <>
      <header className="fixed top-0 z-50 w-full border-b border-slate-200/70 bg-white/90 shadow-sm backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/90">
        <NavbarTopBar
          settings={settings}
          user={user}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          theme={theme}
          unreadCount={unreadCount}
          unreadMessageCount={unreadMessageCount}
          unreadCountContactForm={unreadCountContactForm}
          bumpMessages={bumpMessages}
          bumpNotifications={bumpNotifications}
          bumpContactForms={bumpContactForms}
          onThemeToggle={handleThemeToggle}
          onLogout={handleLogout}
        />

        <NavbarMainBar
          settings={settings}
          logoSrc={logoSrc}
          pagesActives={pagesActives}
          isAuthenticated={isAuthenticated}
          IconComponent={IconComponent}
          mobileOpen={mobileOpen}
          onToggleMobile={() => setMobileOpen((previous) => !previous)}
          onScrollToSection={handleScrollToSection}
          onInscription={handleInscription}
          onApplication={handleApplication}
        />

        {mobileOpen && (
          <NavbarMobileMenu
            settings={settings}
            logoSrc={logoSrc}
            pagesActives={pagesActives}
            user={user}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            IconComponent={IconComponent}
            theme={theme}
            unreadCount={unreadCount}
            unreadMessageCount={unreadMessageCount}
            unreadCountContactForm={unreadCountContactForm}
            onThemeToggle={handleThemeToggle}
            onScrollToSection={handleScrollToSection}
            onInscription={handleInscription}
            onApplication={handleApplication}
            onLogout={handleLogout}
            onClose={closeAllMenus}
          />
        )}
      </header>

      {megaMenuOpen && isAuthenticated && (
        <div
          className="fixed inset-0 z-[60] bg-slate-950/60 px-4 py-24 backdrop-blur-sm"
          onClick={() => setMegaMenuOpen(false)}
        >
          <div
            className="mx-auto w-full max-w-[1280px]"
            onClick={(event) => event.stopPropagation()}
          >
            <MegaMenu
              role_id={user.role_id}
              settings={settings}
              onClose={() => setMegaMenuOpen(false)}
              exercicesRestants={exercicesRestants}
            />
          </div>
        </div>
      )}

      {loginModalOpen && (
        <Modal
          title="Inscription Adhérent"
          onClose={() => setLoginModalOpen(false)}
        >
          <div className="space-y-5 text-center">
            <p className="text-slate-600 dark:text-slate-300">
              Merci de vous connecter à votre compte ou de créer un compte pour
              faire une adhésion.
            </p>

            <div className="grid gap-3 sm:grid-cols-3">
              <button
                type="button"
                onClick={() => setLoginModalOpen(false)}
                className="premium-button-secondary"
              >
                Annuler
              </button>

              <button
                type="button"
                onClick={() => {
                  setLoginModalOpen(false);
                  navigate("/login");
                }}
                className="premium-button-primary"
              >
                Se connecter
              </button>

              <button
                type="button"
                onClick={() => {
                  setLoginModalOpen(false);
                  navigate("/register");
                }}
                className="premium-button-secondary"
              >
                S'inscrire
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}